// callFollowUpMail.ts — zentrale Follow-up-Mailvorlagen für Anrufe (SP5, A10).
//
// Zwei höfliche deutsche Vorlagen, ausgewählt nach der Anruf-RICHTUNG:
//   (a) AUSGEHEND_NICHT_ERREICHT — ich habe versucht, den Kontakt zu erreichen
//       (Mailbox/nicht abgenommen): „…wann erreiche ich Sie am besten / ich
//       versuche es die Tage erneut". Dies ist die aus `ContactKommunikationView`
//       hierher umgezogene `NICHT_ERREICHT_MAIL`.
//   (b) EINGEHEND_VERPASST — der Kontakt hat uns angerufen, wir haben den Anruf
//       verpasst: „…leider haben wir Ihren Anruf verpasst. Wann erreichen wir
//       Sie am besten?".
//
// Die Vorlagen liefern Betreff + HTML-Body; der Body öffnet das bestehende
// Compose-Fenster VORBEFÜLLT (to/subject/body) — der Versand passiert dort
// BEWUSST durch den Nutzer (kein Auto-Send). Im Compose steht zusätzlich der
// bestehende Mail-Vorlagen-Picker (editierbare `mail_template`-Einträge,
// tenant-/user-scope) zur Verfügung, sodass der Standardtext bei Bedarf durch
// eine gepflegte Vorlage ersetzt werden kann.
//
// A2 (Review 2026-07-06 — Anrede): KEINE generische „Hallo <voller Name>"-Anrede
// mehr (der Anzeigename ist „Nachname, Vorname" bzw. der Firmenname und ergibt
// keinen korrekten Gruß). Stattdessen (siehe `buildFollowUpAnrede`):
//   - Firma                     → neutrale Sammelanrede „Hallo zusammen,".
//   - Person + sicherer Vorname → „Hallo <Vorname>,".
//   - Person ohne Vorname       → KEINE automatische Anrede (nur der Textkörper);
//     der Nutzer ergänzt die persönliche Ansprache bewusst im Compose-Fenster.

import type { CallDirection } from "../../services/telephonyService";

/** Steuerdaten für die Anrede-Ableitung einer Follow-up-Vorlage (A2). */
export interface MailAnredeOptions {
    /** true = Empfänger ist eine Firma → neutrale Sammelanrede. */
    isCompany?: boolean;
    /** Vorname NUR, wenn er sicher als eigenes Feld vorliegt (kein Split von Anzeigenamen). */
    firstName?: string | null;
}

export interface CallFollowUpTemplate {
    subject: string;
    /**
     * HTML-Body der Vorlage. Die Anrede folgt A2 (siehe buildFollowUpAnrede):
     * Firma → „Hallo zusammen,"; Person → nur mit sicherem Vornamen, sonst leer.
     */
    body(opts?: MailAnredeOptions): string;
}

/** Minimaler HTML-Escape für den in die Anrede eingesetzten Namen. */
function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/**
 * Baut die Vorlagen-Anrede nach A2:
 *   - Firma                     → „Hallo zusammen,"
 *   - Person + sicherer Vorname → „Hallo <Vorname>,"
 *   - Person ohne Vorname       → "" (keine automatische Anrede)
 *
 * Rückgabe ist bereits HTML-escaped und OHNE umschließendes `<p>`.
 */
export function buildFollowUpAnrede(opts?: MailAnredeOptions): string {
    // Firma hat Vorrang: auch wenn (versehentlich) ein Vorname mitkäme, bleibt
    // es bei der neutralen Sammelanrede.
    if (opts?.isCompany) return "Hallo zusammen,";
    const firstName = (opts?.firstName ?? "").trim();
    if (firstName) return `Hallo ${escapeHtml(firstName)},`;
    return "";
}

// ── (a) Ausgehend nicht erreicht / Mailbox ──────────────────────────────────
export const AUSGEHEND_NICHT_ERREICHT: CallFollowUpTemplate = {
    subject: "Ich habe versucht, Sie zu erreichen",
    body(opts?: MailAnredeOptions): string {
        const anrede = buildFollowUpAnrede(opts);
        // Ohne Anrede eröffnet der Textkörper selbst die Nachricht → erster
        // Buchstabe groß; mit Anrede folgt der Satz klein auf den Gruß.
        const opener = anrede ? "ich" : "Ich";
        return (
            (anrede ? `<p>${anrede}</p>` : "") +
            `<p>${opener} habe gerade versucht, Sie telefonisch zu erreichen – leider ohne Erfolg. ` +
            `Ich melde mich morgen gerne erneut bei Ihnen.</p>` +
            `<p>Falls es Ihnen passt, können Sie mich auch jederzeit zurückrufen oder ` +
            `mir kurz antworten, wann ich Sie am besten erreiche.</p>` +
            `<p>Mit freundlichen Grüßen</p>`
        );
    },
};

// ── (b) Eingehend verpasst ──────────────────────────────────────────────────
export const EINGEHEND_VERPASST: CallFollowUpTemplate = {
    subject: "Wir haben Ihren Anruf leider verpasst",
    body(opts?: MailAnredeOptions): string {
        const anrede = buildFollowUpAnrede(opts);
        // Der erste Satz beginnt mit der Höflichkeitsform „Sie" (immer groß),
        // daher ist hier keine Opener-Anpassung nötig.
        return (
            (anrede ? `<p>${anrede}</p>` : "") +
            `<p>Sie haben vorhin versucht, uns telefonisch zu erreichen – leider haben wir ` +
            `Ihren Anruf verpasst. Das tut uns leid.</p>` +
            `<p>Gerne kümmern wir uns um Ihr Anliegen. Wann erreichen wir Sie am besten ` +
            `für einen Rückruf? Alternativ können Sie uns hier auch kurz schreiben, worum ` +
            `es geht.</p>` +
            `<p>Mit freundlichen Grüßen</p>`
        );
    },
};

/**
 * Wählt die passende Follow-up-Vorlage nach Anruf-Richtung:
 * eingehend → verpasster Anruf (b), sonst → nicht erreicht (a).
 */
export function callFollowUpTemplate(direction: CallDirection): CallFollowUpTemplate {
    return direction === "inbound" ? EINGEHEND_VERPASST : AUSGEHEND_NICHT_ERREICHT;
}

/**
 * Rückwärtskompatibler Alias: `ContactKommunikationView` nutzt weiterhin
 * dieselbe (hierher verschobene) „nicht erreicht"-Vorlage.
 */
export const NICHT_ERREICHT_MAIL = AUSGEHEND_NICHT_ERREICHT;
